import {
  ARENA_CX,
  ARENA_H,
  ARENA_W,
  HIT_BOX_H,
  HIT_BOX_W,
  type Role,
} from "@duelgame/shared";
import type { Bullet, CowboyDummy, DuelState, Fighter } from "./duel";
import type { DuelController } from "./sync";
import {
  ARM_ANCHOR,
  DANCE_FRAME_MS,
  DANCE_FRAMES,
  DEAD_H,
  DEAD_W,
  FRAME_H,
  FRAME_W,
  FEET_OFFSET_Y,
  STEP_FRAMES,
  getArmSprite,
  getDecorSprite,
  getFighterSprite,
  getSprite,
  spritesReady,
} from "./sprites";
import { DECOR_SIZE } from "./decor";
import {
  ABILITY_COWBOY_DUMMY,
  ABILITY_SHOTGUN,
  DUMMY_INFLATE_MS,
  SHOTGUN_TRACER_LEN,
} from "./abilities";
import { flashbangPos, flashbangWhiteAlpha } from "./flashbang";
import { TUMBLEWEED_SIZE } from "./tumbleweed";
import {
  JACKRABBIT_FRAME_H,
  JACKRABBIT_FRAME_W,
  jackrabbitFrame,
  jackrabbitHop,
} from "./jackrabbit";
import {
  SNAKE_FRAME_H,
  SNAKE_FRAME_W,
  SNAKE_FRAMES,
  snakeFrame,
} from "./snake";

const W = ARENA_W;
const H = ARENA_H;
const FONT = "'Pixel-Western', monospace";
const BUBBLE_FONT = "'VCR OSD Mono', monospace";

/** Debug overlay: fighter collision boxes (toggle with L). */
let showHitboxes = false;

export function toggleHitboxDebug(): void {
  showHitboxes = !showHitboxes;
}

export function renderDuel(
  ctx: CanvasRenderingContext2D,
  state: DuelState,
  role: Role,
  ctrl: DuelController,
  seriesWins: { host: number; guest: number } | null = null
): void {
  ctx.clearRect(0, 0, W, H);
  ctx.imageSmoothingEnabled = false;
  drawArenaBackdrop(ctx);
  drawDecor(ctx, state);
  drawTumbleweeds(ctx, ctrl);
  drawJackrabbits(ctx, ctrl);
  drawSnake(ctx, ctrl);

  const peek = ctrl.getSneakPeekReveal();
  const remoteVisible = ctrl.isRemoteVisible();
  const me = role === "host" ? state.host : state.guest;
  const them = role === "host" ? state.guest : state.host;

  drawDust(ctx, ctrl, false);

  drawDummies(ctx, state, role, peek);

  if (state.localReady) {
    drawFighter(
      ctx,
      me,
      true,
      state,
      role,
      ctrl.wearsCactusSuit(true),
      ctrl.hasAbility(ABILITY_SHOTGUN)
    );
  }
  if (state.remoteReady && remoteVisible) {
    drawFighter(
      ctx,
      them,
      false,
      state,
      role,
      ctrl.wearsCactusSuit(false),
      ctrl.peerHasAbility(ABILITY_SHOTGUN)
    );
  }

  // Costume-change burst draws over the fighter.
  drawDust(ctx, ctrl, true);

  if (showHitboxes) {
    if (state.localReady) drawHitbox(ctx, me, "#00ff88");
    if (state.remoteReady) drawHitbox(ctx, them, "#ff4488");
  }

  for (const b of state.bullets) {
    if (state.fogActive && !isOnVisibleHalf(b.x, role) && peek < 0.02) continue;
    drawBullet(ctx, b);
  }

  if (state.isNight) {
    drawNightTint(ctx);
    drawNightMist(ctx, ctrl);
  }

  if (state.fogActive) {
    drawFog(ctx, role, 1 - peek);
  }

  // Bubbles above heads (drawn after fog so enemy taunts stay readable)
  if (state.localReady) {
    drawSpeechBubble(ctx, me);
  }
  if (state.remoteReady) {
    drawSpeechBubble(ctx, them);
  }
  drawHorseBubble(ctx, state);

  drawHud(ctx, state, role, ctrl);
  if (seriesWins && state.phase !== "aim") {
    drawSeriesTally(ctx, seriesWins);
  }

  drawFlashbangs(ctx, ctrl);
  drawFlashbangWhiteout(ctx, ctrl, role);
}

function drawNightTint(ctx: CanvasRenderingContext2D): void {
  ctx.save();
  // Warm dusk wash — deep blue-gray with a soft amber cast.
  ctx.fillStyle = "rgba(28, 22, 40, 0.42)";
  ctx.fillRect(0, 0, W, H);
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = "#6a4a3a";
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
}

function drawNightMist(
  ctx: CanvasRenderingContext2D,
  ctrl: DuelController
): void {
  for (const b of ctrl.nightMist.blobs) {
    const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
    // Dark, smoky night fog — deep blue-gray, still translucent.
    g.addColorStop(0, `rgba(24, 20, 34, ${b.alpha})`);
    g.addColorStop(0.55, `rgba(20, 17, 30, ${b.alpha * 0.5})`);
    g.addColorStop(1, "rgba(16, 14, 26, 0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Cloud scroll speed across the menu backdrop (px/s, leftward). */
const CLOUD_SCROLL_SPEED = 12;

export function renderArenaIdle(ctx: CanvasRenderingContext2D): void {
  ctx.imageSmoothingEnabled = false;

  // Sky base — bg.png is transparent there; without this the canvas chrome shows through.
  ctx.fillStyle = "#7eb6e8";
  ctx.fillRect(0, 0, W, H);

  // Slow-scrolling clouds behind the (partly transparent) menu background.
  const cloud = getSprite("bgCloud");
  if (cloud && cloud.naturalWidth > 0) {
    const tileW = cloud.naturalWidth;
    let offset = ((performance.now() / 1000) * CLOUD_SCROLL_SPEED) % tileW;
    if (offset < 0) offset += tileW;
    for (let x = -offset; x < W; x += tileW) {
      ctx.drawImage(cloud, Math.round(x), 0);
    }
  }

  const bg = getSprite("bg");
  if (bg) {
    ctx.drawImage(bg, 0, 0);
  } else if (!cloud) {
    // Fallback while the sprites are still loading
    drawArenaBackdrop(ctx);
  }
}

function drawTumbleweeds(
  ctx: CanvasRenderingContext2D,
  ctrl: DuelController
): void {
  const img = getSprite("tumbleweed");
  if (!img) return;
  const half = TUMBLEWEED_SIZE / 2;
  for (const w of ctrl.tumbleweeds.weeds) {
    // Hop in sync with the roll (angle already tracks distance traveled).
    const bounce = -Math.abs(Math.sin(w.angle * 0.8)) * 14;
    ctx.save();
    ctx.translate(w.x, w.y + bounce);
    ctx.rotate(w.angle);
    ctx.drawImage(img, -half, -half);
    ctx.restore();
  }
}

function drawJackrabbits(
  ctx: CanvasRenderingContext2D,
  ctrl: DuelController
): void {
  const img = getSprite("jackrabbit");
  if (!img) return;
  const ox = -Math.floor(JACKRABBIT_FRAME_W / 2);
  const oy = -Math.floor(JACKRABBIT_FRAME_H / 2);
  for (const r of ctrl.jackrabbits.rabbits) {
    ctx.save();
    ctx.translate(Math.round(r.x), Math.round(r.y + jackrabbitHop(r)));
    if (r.facingLeft) ctx.scale(-1, 1);
    ctx.drawImage(
      img,
      jackrabbitFrame(r) * JACKRABBIT_FRAME_W,
      0,
      JACKRABBIT_FRAME_W,
      JACKRABBIT_FRAME_H,
      ox,
      oy,
      JACKRABBIT_FRAME_W,
      JACKRABBIT_FRAME_H
    );
    ctx.restore();
  }
}

function drawSnake(ctx: CanvasRenderingContext2D, ctrl: DuelController): void {
  const s = ctrl.snake.snake;
  if (!s || !s.alive) return;
  const img = getSprite("snake");
  if (!img || img.naturalWidth <= 0) return;
  const fw = Math.floor(img.naturalWidth / SNAKE_FRAMES) || SNAKE_FRAME_W;
  const fh = img.naturalHeight || SNAKE_FRAME_H;
  const frame = snakeFrame(s);
  const ox = -Math.floor(fw / 2);
  const oy = -Math.floor(fh / 2);
  ctx.save();
  ctx.translate(Math.round(s.x), Math.round(s.y));
  // Sheet faces right; rotate toward the hunt target.
  ctx.rotate(s.angle);
  ctx.drawImage(img, frame * fw, 0, fw, fh, ox, oy, fw, fh);
  ctx.restore();
}

function drawFlashbangs(
  ctx: CanvasRenderingContext2D,
  ctrl: DuelController
): void {
  const now = performance.now();
  for (const fb of ctrl.getFlashbangs()) {
    const p = flashbangPos(fb, now);
    if (!p) continue;
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.fillStyle = "#5a1010";
    ctx.fillRect(-4, -10, 8, 20);
    ctx.strokeStyle = "#2d0808";
    ctx.lineWidth = 1;
    ctx.strokeRect(-4, -10, 8, 20);
    ctx.restore();
  }
}

function drawFlashbangWhiteout(
  ctx: CanvasRenderingContext2D,
  ctrl: DuelController,
  role: Role
): void {
  const alpha = flashbangWhiteAlpha(ctrl.getFlashbangs(), role, performance.now());
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
}

function isOnVisibleHalf(x: number, role: Role): boolean {
  if (role === "host") return x < ARENA_CX;
  return x >= ARENA_CX;
}

function drawArenaBackdrop(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = "#c4a574";
  ctx.fillRect(0, 0, W, H);

  // Border
  ctx.strokeStyle = "#5a3a1a";
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, W - 6, H - 6);

  // Center line (vertical — left vs right)
  ctx.strokeStyle = "rgba(42, 31, 20, 0.35)";
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 10]);
  ctx.beginPath();
  ctx.moveTo(ARENA_CX, 12);
  ctx.lineTo(ARENA_CX, H - 12);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawDecor(ctx: CanvasRenderingContext2D, state: DuelState): void {
  if (state.decor.length === 0) return;
  for (const prop of state.decor) {
    const img = getDecorSprite(prop.type, prop.variant);
    if (!img) continue;
    const w = img.naturalWidth || DECOR_SIZE;
    const h = img.naturalHeight || DECOR_SIZE;
    const dx = Math.round(prop.x);
    const dy = Math.round(prop.y - h);
    // Fog (drawn later) covers the opponent's half during countdown
    if (prop.facingLeft) {
      ctx.save();
      ctx.translate(dx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -Math.round(w / 2), dy);
      ctx.restore();
    } else {
      ctx.drawImage(img, Math.round(prop.x - w / 2), dy);
    }
  }
}

function drawDummies(
  ctx: CanvasRenderingContext2D,
  state: DuelState,
  role: Role,
  peek: number
): void {
  if (state.dummies.length === 0) return;
  const now = performance.now();
  for (const d of state.dummies) {
    if (state.fogActive && !isOnVisibleHalf(d.x, role) && peek < 0.02) continue;
    drawDummy(ctx, d, d.owner === role, now);
  }
}

function drawDummy(
  ctx: CanvasRenderingContext2D,
  d: CowboyDummy,
  isLocal: boolean,
  now: number
): void {
  const img = getSprite(isLocal ? "dummy" : "enemyDummy");
  if (!img) return;

  const t = Math.min(1, Math.max(0, (now - d.placedAtMs) / DUMMY_INFLATE_MS));
  // Ease out + slight overshoot so it "pops" as it inflates
  const ease = 1 - Math.pow(1 - t, 3);
  const scale = 0.08 + ease * 1.02;
  const s = t >= 1 ? 1 : Math.min(1.08, scale);

  const w = img.naturalWidth || FRAME_W;
  const h = img.naturalHeight || FRAME_H;
  const x = Math.round(d.x);
  const y = Math.round(d.y);
  // Grow from the feet so it looks like it's standing up
  const feetY = y + h / 2;

  ctx.save();
  ctx.translate(x, feetY);
  if (d.facingLeft) ctx.scale(-1, 1);
  ctx.scale(s, s);
  ctx.drawImage(img, -w / 2, -h);
  ctx.restore();
}

function drawHitbox(
  ctx: CanvasRenderingContext2D,
  f: Fighter,
  color: string
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.strokeRect(
    f.x - HIT_BOX_W / 2,
    f.y - HIT_BOX_H / 2,
    HIT_BOX_W,
    HIT_BOX_H
  );
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(f.x, f.y, 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawDust(
  ctx: CanvasRenderingContext2D,
  ctrl: DuelController,
  above: boolean
): void {
  const dustImg = getSprite("dust");
  const smokeImg = getSprite("smoke");
  ctx.save();
  if (!above) {
    for (const s of ctrl.dust.popStreaks) {
      const t = s.life / s.maxLife;
      const speed = Math.hypot(s.vx, s.vy) || 1;
      const ux = s.vx / speed;
      const uy = s.vy / speed;
      const len = s.length * (0.7 + 0.3 * t);
      ctx.globalAlpha = Math.min(1, t * 1.2);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(s.x - ux * len, s.y - uy * len);
      ctx.lineTo(s.x, s.y);
      ctx.stroke();
    }
  }
  for (const p of ctrl.dust.all) {
    if (p.above !== above) continue;
    const img = p.kind === "smoke" ? smokeImg : dustImg;
    if (!img) continue;
    const t = p.life / p.maxLife;
    ctx.globalAlpha =
      p.kind === "smoke" ? Math.min(0.55, t * 0.7) : Math.min(0.35, t * 0.5);
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.drawImage(img, -p.size / 2, -p.size / 2, p.size, p.size);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
  ctx.restore();
}

function drawFog(
  ctx: CanvasRenderingContext2D,
  role: Role,
  alpha = 1
): void {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = Math.min(1, Math.max(0, alpha));
  ctx.fillStyle = "#080604";
  if (role === "host") {
    // Hide right half (guest side)
    ctx.fillRect(ARENA_CX, 0, W - ARENA_CX, H);
  } else {
    // Hide left half (host side)
    ctx.fillRect(0, 0, ARENA_CX, H);
  }
  ctx.restore();
}

/** Speech bubble above a fighter, fixed where it appeared during fog. */
function drawSpeechBubble(ctx: CanvasRenderingContext2D, f: Fighter): void {
  if (!f.bubbleText) return;
  const x = f.bubbleX ?? f.drawX;
  const y = (f.bubbleY ?? f.drawY) - FRAME_H / 2 - 10;
  drawBubbleAt(ctx, x, y, f.bubbleText);
}

/** Distraction line when a horse is on the field and the duel starts. */
function drawHorseBubble(ctx: CanvasRenderingContext2D, state: DuelState): void {
  if (!state.horseBubbleText) return;
  const horseImg = getSprite("horse");
  const h = horseImg?.naturalHeight || DECOR_SIZE;
  const y = state.horseBubbleY - h - 10;
  drawBubbleAt(ctx, state.horseBubbleX, y, state.horseBubbleText);
}

function drawBubbleAt(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  text: string
): void {
  ctx.save();
  ctx.font = `16px ${BUBBLE_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const metrics = ctx.measureText(text);
  const padX = 10;
  const bw = Math.ceil(metrics.width) + padX * 2;
  const bh = 30;
  const margin = 6;
  const bx = Math.round(
    Math.max(margin, Math.min(W - bw - margin, x - bw / 2))
  );
  // Leave room for the tail as well as the bubble itself.
  const by = Math.round(
    Math.max(margin, Math.min(H - bh - 8 - margin, y - bh))
  );

  ctx.fillStyle = "#f5ecd7";
  ctx.strokeStyle = "#2a1f14";
  ctx.lineWidth = 2;
  roundRect(ctx, bx, by, bw, bh, 6);
  ctx.fill();
  ctx.stroke();

  const tailX = Math.round(Math.max(bx + 12, Math.min(bx + bw - 12, x)));

  ctx.beginPath();
  ctx.moveTo(tailX - 6, by + bh);
  ctx.lineTo(tailX, by + bh + 8);
  ctx.lineTo(tailX + 6, by + bh);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Cover the top stroke of the tail where it meets the bubble
  ctx.fillStyle = "#f5ecd7";
  ctx.fillRect(tailX - 5, by + bh - 2, 10, 4);

  ctx.fillStyle = "#2a1f14";
  ctx.fillText(text, bx + bw / 2, by + bh / 2 + 1);
  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawFighter(
  ctx: CanvasRenderingContext2D,
  f: Fighter,
  isLocal: boolean,
  state: DuelState,
  _role: Role,
  cactusSuit = false,
  shotgun = false
): void {
  const x = f.drawX;
  const y = f.drawY;
  const facing = f.drawFacing;
  const spreadHalf = f.drawSpreadHalf;
  const showCone =
    isLocal && state.phase === "aim" && !f.hasShot && !f.hit;

  if (showCone) {
    drawAccuracyCone(ctx, x, y, facing, spreadHalf);
  }

  if (spritesReady()) {
    drawSpriteFighter(ctx, f, state, isLocal, cactusSuit, shotgun);
  } else {
    drawFallbackFighter(ctx, f, isLocal);
  }

  if (isLocal && f.targetX !== null && f.targetY !== null && state.phase === "maneuver") {
    // Feet → next-step feet (aim direction is also computed from the feet)
    drawPaceArrow(
      ctx,
      x,
      y + FEET_OFFSET_Y,
      f.targetX,
      f.targetY + FEET_OFFSET_Y
    );
  }
}

function drawBullet(ctx: CanvasRenderingContext2D, b: Bullet): void {
  const speed = Math.hypot(b.vx, b.vy);
  if (speed > 0) {
    // Tracer trails behind along the velocity vector, fading out
    const tracerLen = b.shotgun ? SHOTGUN_TRACER_LEN : 280;
    const tailX = b.x - (b.vx / speed) * tracerLen;
    const tailY = b.y - (b.vy / speed) * tracerLen;

    const grad = ctx.createLinearGradient(tailX, tailY, b.x, b.y);
    grad.addColorStop(0, "rgba(255, 255, 255, 0)");
    grad.addColorStop(1, "rgba(255, 255, 255, 0.9)");

    ctx.save();
    ctx.strokeStyle = grad;
    ctx.lineWidth = b.shotgun ? 1.5 : 2.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.restore();
  }

  ctx.fillStyle = "#1a120a";
  ctx.beginPath();
  ctx.arc(b.x, b.y, b.shotgun ? 2 : 3, 0, Math.PI * 2);
  ctx.fill();
}

function drawPaceArrow(
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
): void {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.hypot(dx, dy);
  if (len < 1) return;

  const angle = Math.atan2(dy, dx);
  const headLen = 20;
  const headWidth = 16;
  const tipX = toX;
  const tipY = toY;
  const baseX = tipX - Math.cos(angle) * headLen;
  const baseY = tipY - Math.sin(angle) * headLen;
  const leftX = baseX + Math.cos(angle + Math.PI / 2) * (headWidth / 2);
  const leftY = baseY + Math.sin(angle + Math.PI / 2) * (headWidth / 2);
  const rightX = baseX + Math.cos(angle - Math.PI / 2) * (headWidth / 2);
  const rightY = baseY + Math.sin(angle - Math.PI / 2) * (headWidth / 2);

  ctx.save();
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(leftX, leftY);
  ctx.lineTo(rightX, rightY);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawSpriteFighter(
  ctx: CanvasRenderingContext2D,
  f: Fighter,
  state: DuelState,
  isLocal: boolean,
  cactusSuit: boolean,
  shotgun: boolean
): void {
  const phase = state.phase;
  const x = Math.round(f.drawX);
  const y = Math.round(f.drawY);
  const facingLeft = f.drawFacingLeft;
  const ox = -Math.floor(FRAME_W / 2);
  const oy = -Math.floor(FRAME_H / 2);

  const won =
    (state.outcome === "host_win" && f === state.host) ||
    (state.outcome === "guest_win" && f === state.guest);

  const dancing = phase === "result" && won && !f.hit;
  const aiming = !f.hit && (phase === "aim" || (phase === "result" && !won));
  const stepping =
    !f.hit && phase === "maneuver" && f.drawStepProgress > 0 && f.drawStepCount > 0;

  if (f.hit) {
    const dead = getSprite("dead")!;
    const dox = -Math.floor(DEAD_W / 2);
    const doy = -Math.floor(DEAD_H / 2);
    ctx.save();
    ctx.translate(x, y);
    if (facingLeft) ctx.scale(-1, 1);
    ctx.drawImage(dead, dox, doy);
    ctx.restore();
  } else if (dancing) {
    const sheet = getFighterSprite(isLocal, "dance")!;
    const frame = Math.floor(performance.now() / DANCE_FRAME_MS) % DANCE_FRAMES;
    ctx.save();
    ctx.translate(x, y);
    if (facingLeft) ctx.scale(-1, 1);
    ctx.drawImage(
      sheet,
      frame * FRAME_W,
      0,
      FRAME_W,
      FRAME_H,
      ox,
      oy,
      FRAME_W,
      FRAME_H
    );
    ctx.restore();
  } else if (cactusSuit) {
    // Native size — cactus suits aren't the usual 54×84 frame
    const suit = getSprite(isLocal ? "cactusSuit" : "enemyCactusSuit");
    if (suit) {
      const sox = -Math.floor(suit.naturalWidth / 2);
      const soy = -Math.floor(suit.naturalHeight / 2);
      // Hop up in an arc on each pace (can't walk in a cactus suit!)
      const hop = stepping ? -Math.sin(f.drawStepProgress * Math.PI) * 12 : 0;
      ctx.save();
      ctx.translate(x, y + Math.round(hop));
      if (facingLeft) ctx.scale(-1, 1);
      ctx.drawImage(suit, sox, soy);
      ctx.restore();
    }
  } else if (aiming) {
    const body = getFighterSprite(isLocal, "aiming")!;
    const arm = getArmSprite(isLocal, shotgun)!;

    // Arm uses the same capped aim angle as the cone (drawFacing), not raw mouse.
    const cos = Math.cos(f.drawFacing);
    const sin = Math.sin(f.drawFacing);
    const localAimX = facingLeft ? -cos : cos;
    const localAimY = sin;
    const localAngle = Math.atan2(localAimY, localAimX);

    ctx.save();
    ctx.translate(x, y);
    if (facingLeft) ctx.scale(-1, 1);
    ctx.drawImage(body, ox, oy);
    ctx.translate(ox + ARM_ANCHOR.x, oy + ARM_ANCHOR.y);
    ctx.rotate(localAngle);
    ctx.drawImage(arm, 0, 0);
    ctx.restore();
  } else if (stepping) {
    const sheet = getFighterSprite(
      isLocal,
      f.drawStepCount % 2 === 1 ? "step1" : "step2"
    )!;
    const frame = Math.min(
      STEP_FRAMES - 1,
      Math.floor(f.drawStepProgress * STEP_FRAMES)
    );
    ctx.save();
    ctx.translate(x, y);
    if (facingLeft) ctx.scale(-1, 1);
    ctx.drawImage(
      sheet,
      frame * FRAME_W,
      0,
      FRAME_W,
      FRAME_H,
      ox,
      oy,
      FRAME_W,
      FRAME_H
    );
    ctx.restore();
  } else {
    const idle = getFighterSprite(isLocal, "idle")!;
    ctx.save();
    ctx.translate(x, y);
    if (facingLeft) ctx.scale(-1, 1);
    ctx.drawImage(idle, ox, oy);
    ctx.restore();
  }
}

function drawFallbackFighter(
  ctx: CanvasRenderingContext2D,
  f: Fighter,
  isLocal: boolean
): void {
  const x = f.drawX;
  const y = f.drawY;
  ctx.save();
  ctx.translate(x, y);
  if (f.drawFacingLeft) ctx.scale(-1, 1);
  if (f.hit) ctx.globalAlpha = 0.55;

  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(0, 4, 22, 12, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = isLocal ? "#3d6b8a" : "#8a3d3d";
  ctx.beginPath();
  ctx.ellipse(0, 0, 16, 22, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#2a1f14";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

function drawAccuracyCone(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  facing: number,
  spreadHalf: number
): void {
  // Long enough to leave the arena from any spawn (canvas diagonal ~1100).
  const len = 1400;
  const half = Math.max(spreadHalf, 0.012);
  const a0 = facing - half;
  const a1 = facing + half;

  ctx.save();
  ctx.fillStyle = "rgba(255, 255, 255, 0.22)";
  ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
  ctx.lineWidth = 1.5;

  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + Math.cos(a0) * len, y + Math.sin(a0) * len);
  ctx.arc(x, y, len, a0, a1);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function drawHud(
  ctx: CanvasRenderingContext2D,
  state: DuelState,
  role: Role,
  ctrl: DuelController
): void {
  if (state.phase === "result") return;

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  if (state.phase === "waiting") {
    if (state.localReady && !state.remoteReady) {
      hudLabel(ctx, "Waiting for opponent…");
    }
  }

  if (state.phase === "settle") {
    hudLabel(ctx, "Hold...");
  }

  if (state.phase === "maneuver" && state.countdownDisplay !== null) {
    drawManeuverCountdown(ctx, state);
    if (ctrl.hasAbility(ABILITY_COWBOY_DUMMY) && !ctrl.hasPlacedCowboyDummy()) {
      hudLabelAt(ctx, "Press 'space' to place your cowboy dummy", 76);
    }
  }

  if (state.phase === "aim") {
    const me = role === "host" ? state.host : state.guest;
    if (!me.hit && !me.hasShot) {
      hudLabel(ctx, "Shoot!");
    }
  }
}

/** Host wins top-left, guest wins top-right (matches arena sides). */
function drawSeriesTally(
  ctx: CanvasRenderingContext2D,
  wins: { host: number; guest: number }
): void {
  ctx.save();
  ctx.fillStyle = "#f5ecd7";
  ctx.font = `22px ${FONT}`;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  fillPixelText(ctx, String(wins.host), 18, 28);
  ctx.textAlign = "right";
  fillPixelText(ctx, String(wins.guest), W - 18, 28);
  ctx.restore();
}

function drawManeuverCountdown(ctx: CanvasRenderingContext2D, state: DuelState): void {
  const n = state.countdownDisplay!;
  const urgent = n <= 3 && n > 0;

  const now = performance.now();
  const rem = state.maneuverEndMs !== null ? Math.max(0, state.maneuverEndMs - now) : 0;
  const sinceTick = (1000 - (rem % 1000)) % 1000;
  const punch = Math.max(0, 1 - sinceTick / 220);

  hudLabel(ctx, "Take 10 paces");

  // 3 → 76, 2 → 96, 1 → 120; everything else base size.
  let baseSize = 64;
  if (n === 3) baseSize = 76;
  else if (n === 2) baseSize = 96;
  else if (n === 1) baseSize = 120;
  const scale = 1 + punch * (urgent ? 0.22 : 0.18);

  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.scale(scale, scale);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#f5ecd7";
  ctx.font = `${baseSize}px ${FONT}`;
  const label = String(n);
  fillPixelText(ctx, label, 0, 0);
  ctx.restore();
}

function hudLabel(ctx: CanvasRenderingContext2D, text: string): void {
  hudLabelAt(ctx, text, 52);
}

function hudLabelAt(
  ctx: CanvasRenderingContext2D,
  text: string,
  y: number
): void {
  ctx.fillStyle = "#f5ecd7";
  ctx.font = `16px ${FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  fillPixelText(ctx, text, W / 2, y);
}

function fillPixelText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number
): void {
  ctx.fillText(text, Math.round(x), Math.round(y));
}
